/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AsideService } from 'app/layout/components/aside-container/aside.service';
import { CreateUpdateBaseComponent } from 'app/shared/components/create-update-base/create-update-base.component';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { ApiResponseSummary } from 'app/shared/models/api-info.model';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { RefundOption } from 'app/shared/enumeration/refund-option';
import { RefundType } from 'app/shared/enumeration/refund-type';
import { ConfirmationDialogService } from 'app/shared/services/confirmation-dialog.service';
import { RefundFormModel } from '../models/refund-form.model';
import { RefundTicketPriceTypeListComponent } from '../refund-ticket-price-type-list/refund-ticket-price-type-list.component';
import { RefundFormService } from '../services/refund-form.service';
import { SnackBarService } from 'app/shared/services/snack-bar.service';

@Component({
	selector: 'refund-form',
	templateUrl: './refund-form.component.html',
	styleUrls: ['./refund-form.component.scss'],
	standalone: false,
})
export class RefundFormComponent extends CreateUpdateBaseComponent<RefundFormModel> implements OnInit {
	refundOptionLookupData: any[];
	refundTypeLookupData: any[];
	dataModel: RefundFormModel;

	@ViewChild('payInFullTickets') payInFullTicketsComponent: RefundTicketPriceTypeListComponent;
	@ViewChild('paymentPlanTickets') paymentPlanTicketsComponent: RefundTicketPriceTypeListComponent;

	displayedColumns: string[] = [
		'ticketType',
		'ticketNumber',
		'ticketPrice',
		'ticketPricePaid',
		'ticketRefundType',
		'bookingFee',
		'bookingFeePaid',
		'bookingFeeRefundType',
		'venueLevy',
		'venueLevyPaid',
		'venueLevyFeeRefundType',
		'paymentStatus',
		'payInFullTicketstatus',
		'dispatchStatus',
	];
	constructor(protected fb: FormBuilder, private service: RefundFormService, protected asideService: AsideService, protected formHelperService: ReactiveFormHelperService, protected confirmationDialogService: ConfirmationDialogService, protected snackBarService: SnackBarService) {
		super(fb, asideService, formHelperService, confirmationDialogService);
	}

	// shortcuts to controls for readability
	get refundOption(): AbstractControl {
		return this.dataForm.controls['refundOption'];
	}

	get processingFeeRefundType(): AbstractControl {
		return this.dataForm.controls['processingFeeRefundType'];
	}

	get dispatchMethodRefundType(): AbstractControl {
		return this.dataForm.controls['dispatchMethodRefundType'];
	}

	get cancellationProtectionRefundType(): AbstractControl {
		return this.dataForm.controls['cancellationProtectionRefundType'];
	}

	get charityDonationRefundType(): AbstractControl {
		return this.dataForm.controls['charityDonationRefundType'];
	}

	get payInFullTicketsFormArray(): FormArray {
		return this.dataForm.get('payInFullTickets') as FormArray;
	}

	get paymentPlanTicketsFormArray(): FormArray {
		return this.dataForm.get('paymentPlanTickets') as FormArray;
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createFormGroup(): FormGroup<any> {
		return this.fb.group({
			refundOption: [RefundOption.ClearAllRefunds],
			processingFeeRefundType: [RefundType.NotRefunded],
			dispatchMethodRefundType: [RefundType.NotRefunded],
			cancellationProtectionRefundType: [RefundType.NotRefunded],
			charityDonationRefundType: [RefundType.NotRefunded],
			payInFullTickets: this.fb.array([]),
			paymentPlanTickets: this.fb.array([]),
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected populateControls(datafrom: FormGroup<any>) {
		this.refundOptionLookupData = this.formHelperService.createLookupData(this.viewModel.refundOptionLookupData);
		this.refundTypeLookupData = this.formHelperService.createLookupData(this.viewModel.refundTypeLookupData);

		this.dataForm = this.fb.group({
			refundOption: [RefundOption.ClearAllRefunds],
			processingFeeRefundType: [this.viewModel.processingFeeRefundType],
			dispatchMethodRefundType: [this.viewModel.dispatchMethodRefundType],
			cancellationProtectionRefundType: [this.viewModel.cancellationProtectionRefundType],
			charityDonationRefundType: [this.viewModel.charityDonationRefundType],
			payInFullTickets: this.fb.array(this.viewModel.payInFullTickets.map((ticket) => this.createTicketForm(ticket))),
			paymentPlanTickets: this.fb.array(this.viewModel.paymentPlanTickets.map((ticket) => this.createTicketForm(ticket))),
		});
		this.refundOption.setValue(this.viewModel.refundOption);

		this.dataForm.valueChanges
			.pipe(
				switchMap(() => timer(500)) // wait 500ms after last change
			)
			.subscribe(() => {
				this.calculateRefundTotals();
			});
		this.refundOption?.valueChanges.subscribe((value) => {
			this.setRefundTypeDefault(value);
		});
		this.disablePreviouslyRefundedControls();
	}
	private disablePreviouslyRefundedControls() {
		if (this.viewModel.processingFeeRefundType == RefundType.Customer || this.viewModel.processingFeeRefundType == RefundType.Retained) {
			this.processingFeeRefundType.disable();
		}
		if (this.viewModel.dispatchMethodRefundType == RefundType.Customer || this.viewModel.dispatchMethodRefundType == RefundType.Retained) {
			this.dispatchMethodRefundType.disable();
		}
		if (this.viewModel.cancellationProtectionRefundType == RefundType.Customer || this.viewModel.cancellationProtectionRefundType == RefundType.Retained) {
			this.cancellationProtectionRefundType.disable();
		}
		if (this.viewModel.charityDonationRefundType == RefundType.Customer || this.viewModel.charityDonationRefundType == RefundType.Retained) {
			this.charityDonationRefundType.disable();
		}
	}

	private setRefundTypeDefault(value: any) {
		var refundType = null;
		if (value === RefundOption.FullCustomerRefund) {
			refundType = RefundType.Customer;
		} else if (value === RefundOption.FullInternalRefund) {
			refundType = RefundType.Retained;
		} else if (value === RefundOption.ClearAllRefunds) {
			refundType = RefundType.NotRefunded;
		}

		this.payInFullTicketsComponent?.setRefundTypeControls(refundType);
		this.paymentPlanTicketsComponent?.setRefundTypeControls(refundType);

		if (this.processingFeeRefundType.enabled) {
			this.processingFeeRefundType.setValue(refundType);
		}
		if (this.dispatchMethodRefundType.enabled) {
			this.dispatchMethodRefundType.setValue(refundType);
		}
		if (this.cancellationProtectionRefundType.enabled) {
			this.cancellationProtectionRefundType.setValue(refundType);
		}
		if (this.charityDonationRefundType.enabled) {
			this.charityDonationRefundType.setValue(refundType);
		}
	}

	calculateRefundTotals() {
		var model = this.createUpdateModel();

		this.service.calculateRefundTotals(this.dataModel.id, model).then((response: RefundFormModel) => {
			this.dataModel.customerRefundTotal = response.customerRefundTotal;
			this.dataModel.refundPaymentAmount = response.refundPaymentAmount;
			this.dataModel.retainedRefundTotal = response.retainedRefundTotal;
			this.dataModel.proposedOrderTotal = response.proposedOrderTotal;
			this.dataModel.proposedBalanceOutstanding = response.proposedBalanceOutstanding;
			this.dataModel.proposedRemainingInstalments = response.proposedRemainingInstalments;
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<RefundFormModel> {
		//let dataModel: RefundFormModel;

		await this.service.getRefundForm(id).then((response: RefundFormModel) => {
			this.dataModel = response;
		});

		return Promise.resolve(this.dataModel);
	}

	createTicketForm(ticket: any): FormGroup {
		return this.fb.group({
			id: [ticket.id],
			ticketType: [ticket.ticketType],
			ticketNumber: [ticket.ticketNumber],
			ticketPrice: [ticket.ticketPrice],
			ticketPricePaid: [ticket.ticketPricePaid],
			ticketRefundType: [ticket.ticketRefundType ?? RefundType.NotRefunded],
			bookingFee: [ticket.bookingFee],
			bookingFeePaid: [ticket.bookingFeePaid],
			bookingFeeRefundType: [ticket.bookingFeeRefundType ?? RefundType.NotRefunded],
			venueLevy: [ticket.venueLevy],
			venueLevyPaid: [ticket.venueLevyPaid],
			venueLevyFeeRefundType: [ticket.venueLevyFeeRefundType ?? RefundType.NotRefunded],
			paymentStatus: [ticket.paymentStatus],
			payInFullTicketStatus: [ticket.payInFullTicketStatus],
			dispatchStatus: [ticket.dispatchStatus],
			ticketStatus: [ticket.ticketStatus],
			canRefund: [ticket.canRefund],
		});
	}

	// overidden from CreateUpdateBaseComponent base class
	protected createUpdateModel() {
		const extractRefundTypes = (tickets: any[]) =>
			tickets.map(({ id, ticketRefundType, bookingFeeRefundType, venueLevyFeeRefundType }) => ({
				id,
				ticketRefundType,
				bookingFeeRefundType,
				venueLevyFeeRefundType,
			}));

		return {
			payInFullTickets: extractRefundTypes(this.payInFullTicketsFormArray.value),
			paymentPlanTickets: extractRefundTypes(this.paymentPlanTicketsFormArray.value),
			processingFeeRefundType: this.processingFeeRefundType.disabled ? null : this.processingFeeRefundType.value,
			dispatchMethodRefundType: this.dispatchMethodRefundType.disabled ? null : this.dispatchMethodRefundType.value,
			cancellationProtectionRefundType: this.cancellationProtectionRefundType.disabled ? null : this.cancellationProtectionRefundType.value,
			charityDonationRefundType: this.charityDonationRefundType.disabled ? null : this.charityDonationRefundType.value,
			timestamp: this.viewModel ? this.viewModel.timestamp : null,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		let updateResponse: ApiResponseSummary<any>;

		await this.service.createRefund(id, dataToUpdate).then((response: ApiResponseSummary<any>) => {
			updateResponse = response;
		});

		return Promise.resolve(updateResponse);
	}

	// overidden from CreateUpdateBaseComponent base class
	save() {
		if (this.formHelperService.isFormValid(this.dataForm)) {
			const updateModel = this.createUpdateModel();
			this.doCreate(updateModel).then((response: any) => {
				if (response.success === false) {
					if(response.data?.message)
						this.snackBarService.warning(response.data?.message);
				}
				if(response.data?.message)
					this.snackBarService.info(response.data?.message);
				this.asideService.close('update');
			});
		}
	}
	protected async doCreate(dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		return await this.service.createRefund(this.dataModel.id, dataToUpdate);
	}
}
